import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, FloatingLabel, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { closePasswordResetModal } from '../slicers/PasswordResetSlice';
import useAxios from '../services/AxiosConfig';
import PasswordResetService from '../services/PasswordResetService';
import { showAlert } from '../components/PerinHealthAlertSlice';
import styles from './PasswordResetModal.module.css';

const PasswordResetModal = () => {
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const passwordResetService = PasswordResetService();
    
    const { 
        show, 
        type, 
        message, 
        urgency 
    } = useSelector(state => state.passwordReset);
    
    // Get the logged-in user's email
    const user = useSelector(state => state.userInfo);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        feedback: []
    });
    
    // Forgot password flow states
    const [currentStep, setCurrentStep] = useState(0); // 0: email, 1: code, 2: new password
    const [email, setEmail] = useState('');
    const [confirmationCode, setConfirmationCode] = useState('');

    // Password validation regex
    const passwordRegex = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

    useEffect(() => {
        if (show) {
            setNewPassword('');
            setConfirmPassword('');
            setError('');
            setPasswordStrength({ score: 0, feedback: [] });
            setCurrentStep(0);
            // Auto-populate email from logged-in user
            setEmail(user?.email || user?.username || '');
            setConfirmationCode('');
        }
    }, [show, user]);

    const validatePassword = (password) => {
        return passwordResetService.getPasswordStrength(password);
    };

    const handlePasswordChange = (e) => {
        const password = e.target.value;
        setNewPassword(password);
        setPasswordStrength(validatePassword(password));
    };

    const handleSendEmail = async (e) => {
        e.preventDefault();
        if (!email) {
            setError('Email address not found. Please contact support.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post('forgot-password', {
                username: email
            });

            if (response.data) {
                setCurrentStep(1);
                setError('');
            }
        } catch (error) {
            const errorInfo = error.response?.data;
            if (errorInfo && errorInfo.code === 500 && errorInfo.message.includes("LimitExceededException")) {
                setError('Attempt limit exceeded, please try after some time.');
            } else {
                setError('There was an error sending the verification code. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        if (!confirmationCode || confirmationCode.length !== 6) {
            setError('Please enter a valid 6-digit verification code');
            return;
        }

        setCurrentStep(2);
        setError('');
    };

    const handleResendCode = async () => {
        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post('forgot-password', {
                username: email
            });

            if (response.data) {
                setError('');
                dispatch(showAlert({
                    header: "Success",
                    message: "Verification code resent successfully",
                    type: 'success',
                }));
            }
        } catch (error) {
            setError('Failed to resend verification code. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!passwordRegex.test(newPassword)) {
            setError('Password does not meet security requirements');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post('forgot-password/confirmation', {
                username: email,
                confirmationCode: confirmationCode,
                password: newPassword
            });

            if (response.data) {
                dispatch(closePasswordResetModal());
                dispatch(showAlert({
                    header: "Success",
                    message: "Password reset successfully",
                    type: 'success',
                }));
            }
        } catch (error) {
            const errorInfo = error.response?.data;
            if (errorInfo && errorInfo.code === 500 && errorInfo.message.includes("CodeMismatchException")) {
                setError('Invalid verification code. Please try again.');
            } else {
                setError('Failed to reset password. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        // Allow closing for both required and warning types
        // For required type, we'll show a confirmation or handle differently
        dispatch(closePasswordResetModal());
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength.score <= 2) return '#dc3545';
        if (passwordStrength.score <= 3) return '#ffc107';
        if (passwordStrength.score <= 4) return '#17a2b8';
        return '#28a745';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength.score <= 2) return 'Weak';
        if (passwordStrength.score <= 3) return 'Fair';
        if (passwordStrength.score <= 4) return 'Good';
        return 'Strong';
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case 0: return 'Password Reset';
            case 1: return 'Verify Email';
            case 2: return 'Reset Password';
            default: return 'Password Reset';
        }
    };

    const getStepMessage = () => {
        switch (currentStep) {
            case 0: return 'Please verify your email address to continue';
            case 1: return 'Please enter the code sent to your email';
            case 2: return 'Please enter a new password to reset your account';
            default: return '';
        }
    };

    if (!show) return null;

    return (
        <Modal 
            show={show} 
            onHide={handleClose} 
            backdrop={true}
            keyboard={true}
            centered
            className={styles.passwordResetModal}
        >
            <Modal.Header closeButton={false}>
                <Modal.Title className={styles.modalTitle}>
                    {getStepTitle()}
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                <div className={styles.messageContainer}>
                    {currentStep === 0 && (
                        <>
                            {type !== 'change' && (
                                <Alert 
                                    variant={urgency === 'critical' ? 'danger' : 'warning'}
                                    className={styles.alert}
                                >
                                    <strong>{type === 'required' ? 'Action Required' : 'Warning'}</strong>
                                    <br />
                                    {message}
                                </Alert>
                            )}
                            
                            <p className={styles.stepMessage}>{getStepMessage()}</p>
                            
                                                         <Form onSubmit={handleSendEmail} className={styles.passwordForm}>
                                 <div className={styles.emailDisplay}>
                                     <p className={styles.emailLabel}>Verification code will be sent to:</p>
                                     <p className={styles.emailAddress}>{email}</p>
                                 </div>

                                {error && (
                                    <Alert variant="danger" className={styles.errorAlert}>
                                        {error}
                                    </Alert>
                                )}
                            </Form>
                        </>
                    )}

                    {currentStep === 1 && (
                        <>
                            <p className={styles.stepMessage}>{getStepMessage()}</p>
                            
                            <Form onSubmit={handleVerifyCode} className={styles.passwordForm}>
                                <FloatingLabel label="Verification Code" className={styles.inputGroup}>
                                    <Form.Control
                                        type="text"
                                        value={confirmationCode}
                                        onChange={(e) => setConfirmationCode(e.target.value)}
                                        placeholder="Enter 6-digit code"
                                        className={styles.passwordInput}
                                        maxLength={6}
                                        pattern="[0-9]{6}"
                                        required
                                    />
                                </FloatingLabel>

                                <div className={styles.resendCode}>
                                    <button 
                                        type="button" 
                                        onClick={handleResendCode}
                                        disabled={isLoading}
                                        className={styles.resendButton}
                                    >
                                        Resend Code
                                    </button>
                                </div>

                                {error && (
                                    <Alert variant="danger" className={styles.errorAlert}>
                                        {error}
                                    </Alert>
                                )}
                            </Form>
                        </>
                    )}

                    {currentStep === 2 && (
                        <>
                            <p className={styles.stepMessage}>{getStepMessage()}</p>
                            
                            <Form onSubmit={handleSubmit} className={styles.passwordForm}>
                                <FloatingLabel label="New Password" className={styles.inputGroup}>
                                    <Form.Control
                                        type={showPassword ? "text" : "password"}
                                        value={newPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Enter new password"
                                        className={styles.passwordInput}
                                        pattern="^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$"
                                        required
                                    />
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        className={styles.passwordToggle}
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </Button>
                                </FloatingLabel>

                                {newPassword && (
                                    <div className={styles.passwordStrength}>
                                        <div className={styles.strengthBar}>
                                            <div 
                                                className={styles.strengthFill}
                                                style={{ 
                                                    width: `${(passwordStrength.score / 5) * 100}%`,
                                                    backgroundColor: getPasswordStrengthColor()
                                                }}
                                            />
                                        </div>
                                        <span className={styles.strengthText}>
                                            {getPasswordStrengthText()}
                                        </span>
                                    </div>
                                )}

                                {passwordStrength.feedback.length > 0 && (
                                    <div className={styles.passwordRequirements}>
                                        <small className="text-muted">Requirements:</small>
                                        <ul className={styles.requirementsList}>
                                            {passwordStrength.feedback.map((req, index) => (
                                                <li key={index} className={styles.requirementItem}>
                                                    {req}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <FloatingLabel label="Confirm New Password" className={styles.inputGroup}>
                                    <Form.Control
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Confirm new password"
                                        className={styles.passwordInput}
                                        required
                                    />
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        className={styles.passwordToggle}
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                    </Button>
                                </FloatingLabel>

                                {error && (
                                    <Alert variant="danger" className={styles.errorAlert}>
                                        {error}
                                    </Alert>
                                )}
                            </Form>
                        </>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                {currentStep === 0 && (
                    <>
                        <Button
                            variant="secondary"
                            onClick={handleClose}
                            className={styles.cancelButton}
                        >
                            {type === 'change' ? 'Cancel' : 'Remind Me Later'}
                        </Button>
                                                 <Button
                             variant="primary"
                             onClick={handleSendEmail}
                             disabled={isLoading || !email}
                             className={styles.submitButton}
                         >
                             {isLoading ? 'Sending...' : 'Send Verification Code'}
                         </Button>
                    </>
                )}
                
                {currentStep === 1 && (
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setCurrentStep(0)}
                            className={styles.cancelButton}
                        >
                            Back
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleVerifyCode}
                            disabled={isLoading || !confirmationCode || confirmationCode.length !== 6}
                            className={styles.submitButton}
                        >
                            {isLoading ? 'Verifying...' : 'Verify Code'}
                        </Button>
                    </>
                )}
                
                {currentStep === 2 && (
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword || !passwordRegex.test(newPassword)}
                        className={styles.submitButton}
                    >
                        {isLoading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default PasswordResetModal;
